import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { FetchAPI, FieldAPI, FormAPI, PanelAPI, ToastNotificationAPI, Token } from '@philips-emr/tws-components';
import { Type } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import './provider-accreditation-specialty-controller.css';
import './accreditation-token.css';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

type MyData = {
  nrSequence?: number,
  ieMainSpecialty?: string,
  fieldOfWork?: number,
  nrFieldOfWork?: number,
  dsFieldOfWork?: string,
  specialty?: number,
  cdSpecialty?: number,
};

type MyException = {
  body: string,
};

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: 'provider-accreditation-specialty' })
export default class ProviderAccreditationSpecialtyController extends BaseController {

  @Inject('fetch-field-of-work')
  private fetchFieldOfWork: FetchAPI;

  @Inject('fetch-list-specialty')
  private fetchListSpecialty: FetchAPI;

  @Inject('field-card')
  private fieldCards: FieldAPI;

  @Inject('form-specialty')
  private formSpecialty: FormAPI;

  @Inject('field-field-of-work')
  private fieldOfWork: FieldAPI;

  @Inject('panel-pendencies')
  private panelPendencies: PanelAPI;

  @Inject('toast-notification')
  private toast: ToastNotificationAPI;

  @OnMount('layout-general-information')
  onRootLayoutMount(): void {
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqAccreditation });
    this.fetchListSpecialty.forceFetch();
  }

  @OnChange('field-specialty')
  onChangeSpecialty(event: ChangeEvent<string>): void {
    const specialtyCode = event.value;
    this.context.set({ specialtyCode });
    if(!specialtyCode) {
      this.fieldOfWork.setValue(undefined);
    }
    this.fetchFieldOfWork.forceFetch();
  }

  @OnAfterFetch('fetch-list-specialty')
  OnAfterFetchList(event: FetchEvent): void {
    const data = event.data as unknown as [];
    const isEmpty = isEmptyArrayOrObject(data);
    this.fieldCards.setVisible(!isEmpty);
  }

  @OnCancel('form-specialty')
  onCancelForm(): void {
    this.formSpecialty.setRecord({ });
  }

  @OnSubmit('form-specialty')
  async onSubmitForm(): Promise<void> {
    const record = this.formSpecialty.getRecord() as MyData;
    await this.saveRecord(record);
  }

  @HPMSOnOptionClick('my-data-card')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as MyData;

    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnDynamicContentRender('dynamic-field-mainSpecialty')
  public onDynamicContentRenderField(event: RenderEvent): void {
    const { data, render } = event;
    const { ieMainSpecialty } = data as MyData;

    if (ieMainSpecialty === 'S') {
      render((): ReactElement => this.getToken());
    }
  }

  @OnMount('form-panel')
  onMountForm(event: MountEvent): void {
    const api = event.target as PanelAPI;
    FormViewService.shouldRenderForm(api, AccreditationSteps.SPECIALTIES);
  }

  @HPMSOnCreateMenu('my-data-card')
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;

    if (editable == 'N') {
      shouldRender(false);
    }
  }

  @OnMount('fetch-specialties')
  onMountFetchSpecialties(event: MountEvent): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const api = event.target as FetchAPI;
    api.setParameters({ nrSeqTypeAccreditation });
    api.forceFetch();
  }

  @OnMount('fetch-pendencies')
  onMountFetch(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.SPECIALTIES);
  }

  @OnAfterFetch('fetch-pendencies')
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  private getToken(): ReactElement {
    return <div className="u-token-list-card">
      <Token value="pa.main" type={Type.NOTICE} />
    </div>;
  }

  private editRecord(nrSequence: number): void {
    this.formSpecialty.setRecord({ });
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    void HttpService.get<Record<string, Value>>(Endpoint.GET.SPECIALTY, { nrSeqUser, nrSeqAccreditation, nrSequence })
      .then((resp) => {
        const { body = {} } = resp;
        const specialtyCode = body.cdSpecialty;
        const nrFieldOfWork = body.nrFieldOfWork;
        this.context.set({ specialtyCode, nrFieldOfWork });
        this.formSpecialty.setData(body);
        this.formSpecialty.setRecord(body);
        this.fetchFieldOfWork.forceFetch();
        this.formSpecialty.validate();
      });
  }

  private async saveRecord(record: MyData): Promise<void> {
    try {
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      return HttpService.post(Endpoint.POST.ESTABLISHMENT_SPECIALTY, { nrSeqUser, nrSeqAccreditation }, { body: { ...record } }).then(() => {
        this.formSpecialty.setRecord({ });
        this.fieldCards.setVisible(true);
        this.fetchListSpecialty.forceFetch();
      }).catch(({ body }: MyException) => {
        ToastServiceProviderAccreditation.error(body);
      });
    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
    }
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    try {
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      await HttpService.delete(Endpoint.DELETE.ESTABLISHMENT_SPECIALTY, { nrSeqUser, nrSeqAccreditation, nrSequence });
      this.formSpecialty.setRecord({ });
      this.fetchListSpecialty.forceFetch();
    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
    }
  }
}
